import { Alert, Box, Chip, CircularProgress, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { XIcon } from '@phosphor-icons/react';

import { useAllSiteBlocks } from '@/hooks/use-all-site-blocks';
import { blockRegistry } from '@/components/blocks/registry';
import type { Page } from '@/types/database';

interface DataDrawerProps {
  open: boolean;
  onClose: () => void;
  pages: Page[];
}

// The "Data" destination (spec section 5) — what on this site is bound to
// live Scouting Ops data versus manually typed. A block is only counted as
// bound if it's genuinely wired to a live query (blockRegistry's boundTo),
// not just tagged category: 'widget' (hero/contact are widgets because
// they compose primitives, not because they pull platform data).
export function DataDrawer({ open, onClose, pages }: DataDrawerProps) {
  const { data: blocks, isLoading } = useAllSiteBlocks(pages);
  const boundCount = (blocks ?? []).filter((block) => blockRegistry[block.block_type]?.boundTo).length;
  const totalCount = blocks?.length ?? 0;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}>
      <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          Data
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </Stack>

      <Stack spacing={2.5} sx={{ p: 2.5 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Alert severity={boundCount > 0 ? 'success' : 'info'}>
              {boundCount} of {totalCount} block{totalCount === 1 ? '' : 's'} on this site {boundCount === 1 ? 'pulls' : 'pull'} live
              data from Scouting Ops — the rest {totalCount - boundCount === 1 ? 'is' : 'are'} manually written and won&apos;t update on
              their own.
            </Alert>

            <Stack spacing={2}>
              {pages.map((page) => {
                const pageBlocks = (blocks ?? []).filter((block) => block.page_id === page.id);
                if (pageBlocks.length === 0) return null;
                return (
                  <Box key={page.id}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      {page.title}
                    </Typography>
                    <Stack spacing={0.75}>
                      {pageBlocks.map((block) => {
                        const entry = blockRegistry[block.block_type];
                        return (
                          <Stack key={block.id} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2">{entry?.label ?? block.block_type}</Typography>
                            {entry?.boundTo ? (
                              <Chip label={`Bound · ${entry.boundTo}`} size="small" color="success" variant="outlined" />
                            ) : (
                              <Chip label="Manual" size="small" variant="outlined" />
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>
    </Drawer>
  );
}
